import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, Subject, Subscription } from 'rxjs';
import { AssessmentEdit } from 'src/app/assessment/model/assessment.edit';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { AssessmentType } from '../model/assessmentType';
import { AssessmentTypeList, AssessmentTypeEdit } from 'src/app/assessment/model/assessment-type.edit';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { UntypedFormGroup } from '@angular/forms';
import { merge } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { MessengerService } from './messenger.service';
import {
  EXPRESSION_PLACEHOLDER_PATTERN,
  normalizeInterpolationTemplate,
  stripInterpolationPlaceholders
} from 'src/app/core/utility/formly-patient-context.util';

const assessmentTypesCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class AssessmentService implements OnDestroy
{
  public communityUserRestrictions: Array<{ text: string; value: string; }> = [
    { text: 'None', value: 'NONE' },
    { text: 'Hidden', value: 'HIDDEN' },
    { text: 'Readonly', value: 'READONLY' },
  ];

  private flatFields = [];
  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private messengerService: MessengerService) 
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  /**
   * Get all cached Assessment Types
   */
  @Cacheable(
    {
      cacheBusterObserver: assessmentTypesCacheBuster
    })
  public getCachedAssessmentTypes(): Observable<AssessmentType[]>
  {
    return this.http.get<AssessmentType[]>(`/assessment/types`);
  }

  /**
   * Delete an Assessment Type.
   *
   * @param id Assessment Type id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: assessmentTypesCacheBuster
    })
  public deleteAssessmentType(id: number): Observable<any>
  {
    return this.http.delete<AssessmentTypeList>(`/assessment/type/${id}`);
  }

  /**
   * Save an Assessement Type.
   *
   * @param data Assessment Type to persit
   * @param isNew Indicate if this is a new or update
   */
  @CacheBuster(
    {
      cacheBusterNotifier: assessmentTypesCacheBuster
    })
  public saveAssessmentType(data: any, isNew: boolean, assessmentTypeId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/assessment/type', data);
    }
    else
    {
      return this.http.put(`/assessment/type/${assessmentTypeId}`, data);
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get Assessment by id.
   *
   * @param id Assessment id
   */
  public getAssessment(id: number): Observable<AssessmentEdit>
  {
    return this.http.get<AssessmentEdit>(`/assessment/${id}`);
  }

  /**
   * Delete an assessment.
   *
   * @param id Assessment id
   */
  public deleteAssessment(id: number): Observable<AssessmentEdit>
  {
    return this.http.delete<AssessmentEdit>(`/assessment/${id}`);
  }

  /**
   * Unlock an assessment fulfillment.
   *
   * @param id Assessment id
   */
  public unlockAssessmentFulfillment(id: number): Observable<AssessmentEdit>
  {
    return this.http.put<AssessmentEdit>(`/assessment/fulfillment/${id}/unlock`, {});
  }

  /**
   * Save an Assessment.
   *
   * @param data Assessment to persit
   * @param isNew Indicate if this is a new or update
   */
  public saveAssessment(data: any, isNew: boolean, assessmentId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/assessment', data);
    }
    else
    {
      return this.http.put(`/assessment/${assessmentId}`, data);
    }
  }

  /**
   * Save an Assessment fulfillment.
   *
   * @param data Assessment fulfillment to persit
   */
  public saveAssessmentFulfillment(data: any): Observable<any>
  {
    return this.http.post('/assessment/fulfillment', data);
  }

  /**
   * Update an Assessment fulfillment.
   *
   * @param data Assessment fulfillment to persit
   */
  public updateAssessmentFulfillment(data: any): Observable<any>
  {
    return this.http.put('/assessment/fulfillment', data);
  }

  /**
   * Get Assessment Types.
   */
  public getAssessmentTypesList(): Observable<AssessmentTypeList[]>
  {
    return this.http.get<AssessmentTypeList[]>('/assessment/types/list');
  }

  /**
   * Check if assessment is in use by the Modeler
   *
   * @param data Assessment Id
   */
  public inUseByModeler(id: number): Observable<any>
  {
    return this.http.get(`/assessment/inuse/${id}`);
  }

  /**
   * Get assessment versions by assessment id.
   *
   * @param id Assessment id
   */
  public getAssessmentVersions(id: number): Observable<string[]>
  {
    return this.http.get<string[]>(`/assessment/${id}/versions`);
  }

  /**
   * Get all the assessments completed for a specific version and date range.
   *
   * @param version Assessment version
   * @param fromDate Assessment From Date
   * @param toDate Assessment To Date
   */
  public getCompletedAssessments(assessmentId: number, version: string, fromDate?: Date, toDate?: Date): Observable<any[]>
  {
    const paramsArray = [];
    let params = '';

    paramsArray.push(`version=${version}`);

    if (fromDate !== undefined)
    {
      paramsArray.push(`from=${fromDate.toISOString().replace('Z', '')}`);
    }

    if (toDate !== undefined)
    {
      paramsArray.push(`to=${toDate.toISOString().replace('Z', '')}`);
    }

    if (paramsArray.length > 0)
    {
      params = `?${paramsArray.join('&')}`;
    }
    return this.http.get<any[]>(`/assessment/${assessmentId}/completed${params}`);
  }

  public getCompletedAssessmentsFromMultipleSources(versions: Array<string>, assessmentId: number,
    fromDate?: Date, toDate?: Date): Observable<any>
  {
    const listQueries: any = [];
    versions.forEach(
      version =>
      {
        listQueries.push(this.getCompletedAssessments(assessmentId, version, fromDate, toDate));
      });
    return forkJoin(listQueries);
  }

  /**
   * Check if Assessment Type is in use
   *
   * @param id Assessment Type Id
   */
  public existsAssessmentType(id: number): Observable<any>
  {
    return this.http.get(`/assessment/type/inuse/${id}`);
  }

  /**
   * Get Assessment Type by id.
   *
   * @param id Assessment Type id
   */
  public getAssessmentTypeById(id: number): Observable<AssessmentTypeEdit>
  {
    return this.http.get<AssessmentTypeEdit>(`/assessment/type/${id}`);
  }

  /**
   * Check if Assessment Type Name exists to avoid name duplicates
   *
   * @param name Assessment Type Name
   */
  public existsAssessmentTypeName(name: string): Observable<any>
  {
    return this.http.get(`/assessment/type/exists?name=${name}`);
  }

  /**
   * Delete Assessment fulfillment.
   *
   * @param assessmentFulfillmentId Assessment fulfillment id.
   */
  public deleteAssessmentFulfillment(assessmentFulfillmentId: number): Observable<any>
  {
    return this.http.delete(`/assessment/fulfillment/${assessmentFulfillmentId}`);
  }

  /**
   * This method process the assessment fields and return a flat list of fields.
   * 
   * @param fields Assessment fields
   * @returns Assessment arrays of fields
   */
  public getFlatAssessmentFields(fields: FormlyFieldConfig[]): any[] 
  {
    this.flatFields = [];
    fields.forEach((f: any) => { this.processAssessmentField(f); });
    return this.flatFields;
  }

  /**
   * Registers and resolves ${...} placeholders on labels, HTML (label description), templates, and default values.
   * Stores original templates on fields for re-evaluation when formState.patient changes.
   */
  public applyFormlyInterpolatedExpressions(fields: FormlyFieldConfig[], model?: any, formState?: any): void
  {
    if (!fields?.length)
    {
      return;
    }

    this.scrubDisplayStringsOnFieldTree(fields, model, formState);
    this.sanitizeFieldConfigsBeforeFormlyInit(fields, model, formState);
    fields.forEach((field) => this.processFormlyInterpolatedExpressions(field, null, model, formState));
    this.scrubInvalidMultiValuesFromModel(fields, model);
    this.scrubUnresolvedPlaceholdersFromModel(fields, model);
    this.scrubDisplayStringsOnFieldTree(fields, model, formState);
    this.runInterpolationDisplaySync(fields);
  }

  /**
   * Normalizes multiselect/multicheckbox control values (e.g. comma-separated string -> array/object).
   */
  public normalizeFormControlValue(field: FormlyFieldConfig, value: any): any
  {
    return this.coerceFieldControlValue(field, value);
  }

  /** Safe initial value for Formly when a default cannot be interpreted. */
  public getSafeEmptyControlValue(field: FormlyFieldConfig): any
  {
    if (field.type === 'multicheckbox')
    {
      return null;
    }

    if (field.type === 'multiselect')
    {
      return [];
    }

    return undefined;
  }

  /**
   * Runs work now and again on the next microtask(s) so Formly can attach formControl / options first.
   */
  public runAfterFormlyFieldsAttach(run: () => void, extraMicrotasks = 1): void
  {
    run();
    let remaining = extraMicrotasks;
    const schedule = (): void =>
    {
      if (remaining <= 0)
      {
        return;
      }

      remaining--;
      queueMicrotask(() =>
      {
        run();
        schedule();
      });
    };

    schedule();
  }

  /**
   * Resolves ${...} placeholders in a display string (labels, HTML blocks, templates).
   * @param formStateOrOptions Formly formState object ({ patient }) or full formly [options].
   */
  public resolveInterpolatedString(
    template: string,
    field: FormlyFieldConfig,
    model?: any,
    formStateOrOptions?: any
  ): string
  {
    if (!template || typeof template !== 'string')
    {
      return template ?? '';
    }

    const normalizedTemplate = normalizeInterpolationTemplate(template);
    if (!normalizedTemplate.includes('${'))
    {
      return normalizedTemplate;
    }

    const resolvedFormState = this.resolveInterpolationFormState(field, formStateOrOptions);
    if (!resolvedFormState?.patient)
    {
      return stripInterpolationPlaceholders(normalizedTemplate);
    }

    const regex = new RegExp(EXPRESSION_PLACEHOLDER_PATTERN.source, 'g');

    const resolved = normalizedTemplate.replace(regex, (_match, expressionBody: string) =>
    {
      return this.formatInterpolatedDisplayValue(
        this.evaluateInterpolatedExpression(expressionBody.trim(), field, model, resolvedFormState)
      );
    });

    return stripInterpolationPlaceholders(resolved);
  }

  /**
   * Re-runs Formly checkExpressions (when available), interpolated display strings, and default values after formState changes.
   */
  public triggerFormlyExpressionRefresh(options: FormlyFormOptions, fields: FormlyFieldConfig[]): void
  {
    if (!fields?.length)
    {
      return;
    }

    const refresh = (): void =>
    {
      if (options?.checkExpressions)
      {
        fields.forEach((field) => this.refreshFormlyFieldExpressions(options, field));
      }
      this.syncInterpolatedDisplayProperties(fields);
      this.syncFormlyFieldDefaults(fields, options?.formState);
    };

    this.runAfterFormlyFieldsAttach(refresh);
  }

  /**
   * Pushes values from the Formly model onto built form controls (used when the form mounts after defaults were computed).
   */
  public applyModelValuesToFormControls(fields: FormlyFieldConfig[], model: any): void
  {
    if (!fields?.length || !model)
    {
      return;
    }

    const walk = (field: FormlyFieldConfig, parentTabKey: string | null): void =>
    {
      if (field.fieldGroup?.length)
      {
        const tabKey = field.type === 'tabs' ? String(field.key) : parentTabKey;
        field.fieldGroup.forEach((child) => walk(child, tabKey));
      }

      if (!field.key || !field.formControl)
      {
        return;
      }

      // User-defined forms may nest fields in tabs/groups; leaf controls are synced above.
      // setValue on a FormGroup requires every child key and fails when the model is partial.
      if (field.fieldGroup?.length || field.formControl instanceof UntypedFormGroup)
      {
        return;
      }

      const raw = this.getFieldModelValue(model, field, parentTabKey);
      const value = this.coerceFieldControlValue(field, raw);
      if (value === undefined || value === null)
      {
        this.clearInvalidControlValue(field);
        return;
      }

      if (field.type !== 'checkbox' && field.type !== 'multicheckbox' && value === '')
      {
        return;
      }

      field.formControl.setValue(value, { emitEvent: false });
    };

    fields.forEach((field) => walk(field, null));
  }

  public setFormlyControlsSum(fields: any, form: any): void
  {
    // First we need to create all controls that contains sum
    const sumKeys = [];
    fields.forEach((f: any) => { this.processAssessmentFieldSum(f, form, sumKeys); });

    // If we have sum, we need to add valueChanges events
    if (sumKeys.length > 0)
    {
      for (const key of Object.keys(form.controls))
      {
        this.processFormlyFormControls(sumKeys, form.controls[key], form.controls);
      }
    }
  }

  /**
   * This method set the model default for date and date/time
   * @param fields Formly fields
   * @param model Model object
   */
  public setFormlyModelDefaults(fields: any, model: any): void 
  {
    fields.forEach((f: any) => { this.processFormlyModelDefaults(model, f, null); });
  }

  /**
   * Get all Assessments.
   */
  public getAllAssessmentMinimal(): Observable<any>
  {
    return this.http.get<any>('/assessments/minimal');
  }

  public isValidDefaultValue(field: FormlyFieldConfig, value: any): boolean
  {
    if (value === undefined || value === null)
    {
      return false;
    }

    if (field.type === 'checkbox')
    {
      return typeof value === 'boolean';
    }

    if (field.type === 'multiselect')
    {
      return Array.isArray(value) && value.length > 0 && value.every((entry) =>
      {
        const item = String(entry).trim();
        return item !== '' && !item.includes('${');
      });
    }

    if (field.type === 'multicheckbox')
    {
      return this.isMulticheckboxControlValue(value) && this.multicheckboxHasSelection(value);
    }

    if (field.type === 'date' || field.type === 'datetime')
    {
      return value instanceof Date && !Number.isNaN(value.getTime());
    }

    if (typeof value === 'string')
    {
      const trimmed = value.trim();
      return trimmed !== '' && !trimmed.includes('${');
    }
    return true;
  }

  /** Display-only: uninterpretable values become empty text, never raw objects or placeholders. */
  private formatInterpolatedDisplayValue(value: any): string
  {
    if (value === undefined || value === null || typeof value === 'object')
    {
      return '';
    }

    const text = String(value);
    return text === 'NaN' ? '' : text;
  }

  private stringHadInterpolationTemplate(value: any): boolean
  {
    return typeof value === 'string' && normalizeInterpolationTemplate(value).includes('${');
  }

  /** Text-like controls accept an empty string when a ${...} default did not resolve. */
  private fieldDefaultAcceptsBlank(field: FormlyFieldConfig): boolean
  {
    const type = field.type;
    return type !== 'multiselect' && type !== 'multicheckbox' && type !== 'checkbox' && type !== 'radio';
  }

  private blankDefaultForField(field: FormlyFieldConfig): any
  {
    if (field.type === 'multiselect')
    {
      return [];
    }

    if (field.type === 'multicheckbox' || field.type === 'date' || field.type === 'datetime')
    {
      return null;
    }

    return '';
  }

  private canApplyEvaluatedDefault(field: FormlyFieldConfig, evaluated: any, rawDefault: any): boolean
  {
    const coerced = this.coerceFieldControlValue(field, evaluated);
    if (this.isValidDefaultValue(field, coerced))
    {
      return true;
    }

    return evaluated !== undefined
      && this.stringHadInterpolationTemplate(rawDefault)
      && this.fieldDefaultAcceptsBlank(field);
  }

  private finalizeResolvedDisplayString(resolved: string): string
  {
    if (!resolved?.includes('${'))
    {
      return resolved ?? '';
    }

    return stripInterpolationPlaceholders(resolved);
  }

  /**
   * Syncs resolved labels/HTML after field trees are built. Runs once now (uses __interpolationFormState)
   * and once on the next microtask when Formly has attached field.options.
   */
  private runInterpolationDisplaySync(fields: FormlyFieldConfig[]): void
  {
    const sync = (): void => this.syncInterpolatedDisplayProperties(fields);
    this.runAfterFormlyFieldsAttach(sync, 1);
  }

  /**
   * Resolves or strips ${...} on label/description/template so display never shows raw placeholders.
   */
  private scrubDisplayStringsOnFieldTree(fields: FormlyFieldConfig[], model?: any, formState?: any): void
  {
    const walk = (field: FormlyFieldConfig): void =>
    {
      field.fieldGroup?.forEach(walk);

      if (field.templateOptions)
      {
        const to = { ...field.templateOptions };

        if (typeof to.label === 'string')
        {
          to.label = this.resolveInterpolatedString(to.label, field, model, formState);
        }

        if (typeof to.description === 'string')
        {
          to.description = this.resolveInterpolatedString(to.description, field, model, formState);
        }
        else if (field.type === 'label' && typeof to.label === 'string')
        {
          to.description = to.label;
        }

        if (field.type === 'label')
        {
          to.label = '';
          (to as { hideLabel?: boolean }).hideLabel = true;
        }

        field.templateOptions = to;
      }

      if (typeof field.template === 'string')
      {
        field.template = this.resolveInterpolatedString(field.template, field, model, formState);
      }

      const rawDefault = this.getFieldDefaultValueTemplate(field);
      if (this.stringHadInterpolationTemplate(rawDefault))
      {
        const evaluated = this.evaluateFieldDefaultValue(rawDefault, field, model, formState);
        if (evaluated !== undefined)
        {
          this.applyEvaluatedDefaultValueToField(field, rawDefault, evaluated);
        }
      }
    };

    fields.forEach(walk);
  }

  private scrubUnresolvedPlaceholdersFromModel(fields: FormlyFieldConfig[], model?: any): void
  {
    if (!model)
    {
      return;
    }

    const walk = (field: FormlyFieldConfig, parentTabKey: string | null): void =>
    {
      if (field.fieldGroup?.length)
      {
        const tabKey = field.type === 'tabs' ? String(field.key) : parentTabKey;
        field.fieldGroup.forEach((child) => walk(child, tabKey));
      }

      if (!field.key)
      {
        return;
      }

      const raw = this.getFieldModelValue(model, field, parentTabKey);
      if (typeof raw === 'string' && raw.includes('${'))
      {
        const resolved = this.resolveInterpolatedString(
          raw,
          field,
          model,
          (field as any).__interpolationFormState);
        if (this.fieldDefaultAcceptsBlank(field))
        {
          this.setFieldModelValue(model, field, resolved, parentTabKey);
        }
        else
        {
          this.deleteFieldModelValue(model, field, parentTabKey);
        }
        return;
      }

      const coerced = this.coerceFieldControlValue(field, raw);
      // Keep multicheckbox true/false maps (including all-false after uncheck).
      if (field.type === 'multicheckbox' && this.isMulticheckboxControlValue(coerced))
      {
        return;
      }

      if (!this.isValidDefaultValue(field, coerced))
      {
        this.deleteFieldModelValue(model, field, parentTabKey);
      }
    };

    fields.forEach((field) => walk(field, null));
  }

  /**
   * This recursive function extract all fields including all controls in all tabs (fieldGroup)
   * 
   * @param f Field record to process.
   * @param parentTabKey Parent tab key name used to set model if the date or datetime control is in a tab
   */
  private processFormlyModelDefaults(model: any, f: any, parentTabKey: string): void 
  {
    if (f.fieldGroup) 
    {
      f.fieldGroup.forEach((t: any) => { this.processFormlyModelDefaults(model, t, f?.type === 'tabs' ? f.key : parentTabKey); });
    }
    else if (f.key && f.templateOptions && (f.type === 'date' || f.type === 'datetime') && f.templateOptions.defaultToday
      && !this.hasFieldDefaultValueTemplate(f))
    {
      const now = new Date();
      const dateValue: Date = f.type === 'datetime' ? now : new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);

      // Is control within a tab?
      if (parentTabKey && f.tab)
      {
        if (model[parentTabKey] === undefined) { model[parentTabKey] = {}; }
        model[parentTabKey][f.tab] = merge(model[parentTabKey][f.tab], { [f.key]: dateValue });
      }
      else
      {
        model = merge(model, { [f.key]: dateValue });
      }
    }
  }

  private processFormlyInterpolatedExpressions(field: FormlyFieldConfig, parentTabKey: string | null, model?: any, formState?: any): void
  {
    if (formState !== undefined)
    {
      (field as any).__interpolationFormState = formState;
    }

    if (field.fieldGroup?.length)
    {
      const tabKey = field.type === 'tabs' ? field.key : parentTabKey;
      field.fieldGroup.forEach((child) => this.processFormlyInterpolatedExpressions(child, tabKey as string, model, formState));
    }

    this.applyFieldDefaultValue(field, parentTabKey, model, formState);
    this.applyInterpolatedExpression(field, 'templateOptions.label', field.templateOptions?.label, formState);

    const description = field.templateOptions?.description
      ?? (field.type === 'label' ? field.templateOptions?.label : undefined);
    this.applyInterpolatedExpression(field, 'templateOptions.description', description, formState);
    this.applyInterpolatedExpression(field, 'template', field.template, formState);
  }

  private applyFieldDefaultValue(field: FormlyFieldConfig, parentTabKey: string | null, model?: any, formState?: any): void
  {
    if (!field.key)
    {
      return;
    }

    const rawDefault = this.getFieldDefaultValueTemplate(field);
    if (rawDefault === undefined || rawDefault === null)
    {
      return;
    }

    if (model)
    {
      this.applyFieldDefaultToModel(field, model, formState, parentTabKey);
    }

    this.applyFieldDefaultHooks(field);
  }

  private applyFieldDefaultToModel(field: FormlyFieldConfig, model: any, formState: any, parentTabKey: string | null): void
  {
    if (this.fieldModelHasValue(model, field, parentTabKey))
    {
      return;
    }

    const rawDefault = this.getFieldDefaultValueTemplate(field);
    const value = this.evaluateFieldDefaultValue(rawDefault, field, model, formState);
    if (value === undefined || !this.canApplyEvaluatedDefault(field, value, rawDefault))
    {
      return;
    }

    const controlValue = this.coerceFieldControlValue(field, value);
    this.setFieldModelValue(model, field, controlValue, parentTabKey);
    this.applyEvaluatedDefaultValueToField(field, rawDefault, controlValue);
  }

  private applyFieldDefaultHooks(field: FormlyFieldConfig): void
  {
    const rawDefault = this.getFieldDefaultValueTemplate(field);
    if (rawDefault === undefined || rawDefault === null)
    {
      return;
    }

    const previousOnInit = field.hooks?.onInit;
    const previousOnChanges = field.hooks?.onChanges;

    field.hooks = {
      ...field.hooks,
      onInit: (f) =>
      {
        if (typeof previousOnInit === 'function')
        {
          previousOnInit(f);
        }
        this.deferFieldDefaultValueSync(f);
      },
      onChanges: (f) =>
      {
        if (typeof previousOnChanges === 'function')
        {
          previousOnChanges(f);
        }
        this.syncFieldDefaultValue(f);
      }
    };
  }

  private deferFieldDefaultValueSync(field: FormlyFieldConfig): void
  {
    const sync = (): void => this.syncFieldDefaultValue(field);
    this.runAfterFormlyFieldsAttach(sync, 1);
  }

  private syncFieldDefaultValue(field: FormlyFieldConfig, formState?: any): void
  {
    if (!field.formControl || !field.key)
    {
      return;
    }

    if (this.formControlHasValue(field))
    {
      return;
    }

    const rawDefault = this.getFieldDefaultValueTemplate(field);
    if (rawDefault === undefined || rawDefault === null)
    {
      return;
    }

    const resolvedFormState = this.resolveInterpolationFormState(field, formState);
    const value = this.evaluateFieldDefaultValue(rawDefault, field, field.model, resolvedFormState);
    if (value === undefined || !this.canApplyEvaluatedDefault(field, value, rawDefault))
    {
      return;
    }

    const controlValue = this.coerceFieldControlValue(field, value);
    if (!this.canApplyEvaluatedDefault(field, controlValue, rawDefault))
    {
      this.clearInvalidControlValue(field);
      return;
    }

    this.applyEvaluatedDefaultValueToField(field, rawDefault, controlValue);
    field.formControl.setValue(controlValue, { emitEvent: false });
    this.setFieldModelValue(field.model, field, controlValue, this.getParentTabKeyFromField(field));
  }

  /**
   * Original default template (with ${...}) for re-evaluation; avoids Formly initializing controls from unevaluated strings.
   */
  private getFieldDefaultValueTemplate(field: FormlyFieldConfig): any
  {
    return (field as any).__defaultValueTemplate ?? field.defaultValue ?? field.templateOptions?.defaultValue;
  }

  private hasFieldDefaultValueTemplate(field: FormlyFieldConfig): boolean
  {
    const raw = this.getFieldDefaultValueTemplate(field);
    if (raw === undefined || raw === null)
    {
      return false;
    }

    return !(typeof raw === 'string' && raw.trim() === '');
  }

  private applyEvaluatedDefaultValueToField(field: FormlyFieldConfig, rawDefault: any, evaluated: any): void
  {
    if (rawDefault !== undefined && rawDefault !== null && typeof rawDefault === 'string')
    {
      (field as any).__defaultValueTemplate = rawDefault;
    }

    if (!this.canApplyEvaluatedDefault(field, evaluated, rawDefault))
    {
      delete field.defaultValue;
    }
    else
    {
      field.defaultValue = this.coerceFieldControlValue(field, evaluated);
    }

    if (field.templateOptions && field.templateOptions.defaultValue !== undefined)
    {
      const templateOptions = { ...field.templateOptions };
      delete templateOptions.defaultValue;
      field.templateOptions = templateOptions;
    }
  }

  /**
   * Evaluates ${...} defaults before Formly creates controls so unevaluated placeholders are never shown.
   */
  private sanitizeFieldConfigsBeforeFormlyInit(fields: FormlyFieldConfig[], model?: any, formState?: any): void
  {
    const walk = (field: FormlyFieldConfig, parentTabKey: string | null): void =>
    {
      if (field.fieldGroup?.length)
      {
        const tabKey = field.type === 'tabs' ? String(field.key) : parentTabKey;
        field.fieldGroup.forEach((child) => walk(child, tabKey));
      }

      if (!field.key)
      {
        return;
      }

      const raw = this.getFieldDefaultValueTemplate(field);
      if (raw === undefined || raw === null)
      {
        return;
      }

      const evaluated = this.evaluateFieldDefaultValue(raw, field, model, formState);
      if (evaluated === undefined || !this.canApplyEvaluatedDefault(field, evaluated, raw))
      {
        delete field.defaultValue;
        delete (field as any).__defaultValueTemplate;
        if (field.templateOptions?.defaultValue !== undefined)
        {
          const templateOptions = { ...field.templateOptions };
          delete templateOptions.defaultValue;
          field.templateOptions = templateOptions;
        }

        return;
      }

      if (this.stringHadInterpolationTemplate(raw))
      {
        (field as any).__defaultValueTemplate = raw;
      }

      field.defaultValue = this.coerceFieldControlValue(field, evaluated);
      if (field.templateOptions?.defaultValue !== undefined)
      {
        const templateOptions = { ...field.templateOptions };
        delete templateOptions.defaultValue;
        field.templateOptions = templateOptions;
      }
    };

    fields.forEach((field) => walk(field, null));
  }

  private scrubInvalidMultiValuesFromModel(fields: FormlyFieldConfig[], model?: any): void
  {
    if (!model)
    {
      return;
    }

    const walk = (field: FormlyFieldConfig, parentTabKey: string | null): void =>
    {
      if (field.fieldGroup?.length)
      {
        const tabKey = field.type === 'tabs' ? String(field.key) : parentTabKey;
        field.fieldGroup.forEach((child) => walk(child, tabKey));
      }

      if (!field.key || (field.type !== 'multiselect' && field.type !== 'multicheckbox'))
      {
        return;
      }

      const raw = this.getFieldModelValue(model, field, parentTabKey);
      const coerced = this.coerceFieldControlValue(field, raw);
      // Multicheckbox may be an all-false map after uncheck; keep that shape (do not treat as invalid).
      if (field.type === 'multicheckbox' && this.isMulticheckboxControlValue(coerced))
      {
        if (raw !== coerced)
        {
          this.setFieldModelValue(model, field, coerced, parentTabKey);
        }

        return;
      }

      if (!this.isValidDefaultValue(field, coerced))
      {
        this.deleteFieldModelValue(model, field, parentTabKey);
        return;
      }

      if (raw !== coerced)
      {
        this.setFieldModelValue(model, field, coerced, parentTabKey);
      }
    };

    fields.forEach((field) => walk(field, null));
  }

  private deleteFieldModelValue(model: any, field: FormlyFieldConfig, parentTabKey: string | null): void
  {
    const key = this.getFieldModelKey(field);
    if (key === null)
    {
      return;
    }

    if (parentTabKey && (field as any).tab)
    {
      const tab = (field as any).tab;
      if (model[parentTabKey]?.[tab])
      {
        delete model[parentTabKey][tab][key];
      }

      return;
    }

    delete model[key];
  }

  private coerceFieldControlValue(field: FormlyFieldConfig, value: any): any
  {
    if (value === undefined || value === null)
    {
      return undefined;
    }

    if (typeof value === 'string' && value.includes('${'))
    {
      return undefined;
    }

    if (field.type === 'checkbox')
    {
      return typeof value === 'boolean' ? value : undefined;
    }

    if (field.type === 'multiselect')
    {
      if (Array.isArray(value))
      {
        const items = value.map((v) => String(v).trim()).filter((v) => v !== '');
        return items.length ? items : undefined;
      }

      return this.parseMultiselectDefaultValue(value);
    }

    if (field.type === 'multicheckbox')
    {
      if (this.isMulticheckboxControlValue(value))
      {
        // Keep explicit true/false maps (including all-false after user unchecked).
        return value;
      }

      return this.parseMulticheckboxDefaultValue(value);
    }

    if (field.type === 'date' || field.type === 'datetime')
    {
      return this.coerceDateFieldValue(field, value);
    }

    if (typeof value === 'string')
    {
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed;
    }

    return value;
  }

  private coerceDateFieldValue(field: FormlyFieldConfig, value: any): Date | undefined
  {
    if (value instanceof Date)
    {
      if (Number.isNaN(value.getTime()))
      {
        return undefined;
      }

      if (field.type === 'date')
      {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0);
      }

      return value;
    }

    if (typeof value !== 'string')
    {
      return undefined;
    }

    const trimmed = value.trim();
    if (trimmed === '' || trimmed.includes('${'))
    {
      return undefined;
    }

    return this.parseDateDefaultString(trimmed, field.type as string);
  }

  /**
   * Parses default date strings without UTC date-only shift (e.g. 1980-06-24 stays June 24 locally).
   */
  private parseDateDefaultString(value: string, fieldType: string): Date | undefined
  {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnly)
    {
      const local = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]), 0, 0, 0);
      return Number.isNaN(local.getTime()) ? undefined : local;
    }

    const localDateTime = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
    if (localDateTime && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(value))
    {
      const local = new Date(
        Number(localDateTime[1]),
        Number(localDateTime[2]) - 1,
        Number(localDateTime[3]),
        Number(localDateTime[4]),
        Number(localDateTime[5]),
        localDateTime[6] ? Number(localDateTime[6]) : 0);
      return Number.isNaN(local.getTime()) ? undefined : local;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
    {
      return undefined;
    }

    if (fieldType === 'date')
    {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 0, 0, 0);
    }

    return parsed;
  }

  private isMulticheckboxControlValue(value: any): value is Record<string, boolean>
  {
    if (!value || typeof value !== 'object' || Array.isArray(value))
    {
      return false;
    }

    return Object.entries(value).every(([key, entry]) => typeof key === 'string' && typeof entry === 'boolean');
  }

  /** Clears controls that were initialized with an uninterpretable value (e.g. comma-separated string on multicheckbox). */
  private clearInvalidControlValue(field: FormlyFieldConfig): void
  {
    if (!field.formControl)
    {
      return;
    }

    const current = field.formControl.value;
    if (field.type === 'multiselect' && typeof current === 'string')
    {
      field.formControl.setValue([], { emitEvent: false });
      return;
    }

    if (field.type === 'multicheckbox' && (typeof current === 'string' || (current && !this.isMulticheckboxControlValue(current))))
    {
      field.formControl.setValue(null, { emitEvent: false });
    }
  }

  /**
   * Syncs evaluated defaults onto form controls after the form is built or formState changes.
   */
  private syncFormlyFieldDefaults(fields: FormlyFieldConfig[], formState?: any): void
  {
    const walk = (field: FormlyFieldConfig): void =>
    {
      if (field.key && this.getFieldDefaultValueTemplate(field) !== undefined && this.getFieldDefaultValueTemplate(field) !== null)
      {
        this.syncFieldDefaultValue(field, formState);
      }
      field.fieldGroup?.forEach(walk);
    };

    fields.forEach(walk);
  }

  private evaluateFieldDefaultValue(rawDefault: any, field: FormlyFieldConfig, model: any, formState: any): any
  {
    const hadInterpolation = this.stringHadInterpolationTemplate(rawDefault);
    let value: any;

    if (hadInterpolation)
    {
      value = this.resolveInterpolatedString(rawDefault, field, model, formState);
      if (typeof value === 'string' && value.includes('${'))
      {
        value = stripInterpolationPlaceholders(value);
      }
    }
    else if (typeof rawDefault === 'string')
    {
      value = rawDefault.trim() === '' ? undefined : rawDefault;
    }
    else
    {
      value = rawDefault;
    }

    const coerced = this.coerceFieldControlValue(field, value);
    if (this.isValidDefaultValue(field, coerced))
    {
      return coerced;
    }

    if (hadInterpolation && this.fieldDefaultAcceptsBlank(field))
    {
      return this.blankDefaultForField(field);
    }

    return undefined;
  }

  /**
   * Multiselect model values are primitive arrays (option value or dictionary code).
   * Designer stores defaults as a comma-separated string.
   */
  private parseMultiselectDefaultValue(value: any): string[] | undefined
  {
    if (value === undefined || value === null)
    {
      return undefined;
    }

    if (Array.isArray(value))
    {
      const items = value.map((v) => String(v).trim()).filter((v) => v !== '');
      return items.length ? items : undefined;
    }

    if (typeof value === 'string')
    {
      const trimmed = value.trim();
      if (!trimmed)
      {
        return undefined;
      }

      const items = trimmed.split(',').map((s) => s.trim()).filter((s) => s !== '');
      return items.length ? items : undefined;
    }

    return undefined;
  }

  /**
   * Multicheckbox model values are objects keyed by option value (e.g. { value1: true }).
   * Designer stores defaults as a comma-separated list of option values to check.
   */
  private parseMulticheckboxDefaultValue(value: any): Record<string, boolean> | undefined
  {
    const items = this.parseMultiselectDefaultValue(value);
    if (!items?.length)
    {
      return undefined;
    }

    return items.reduce((acc: Record<string, boolean>, key) =>
    {
      acc[key] = true;
      return acc;
    }, {});
  }

  private multicheckboxHasSelection(value: any): boolean
  {
    if (value === undefined || value === null || typeof value !== 'object' || Array.isArray(value))
    {
      return false;
    }

    return Object.values(value).some((v) => v === true);
  }

  private fieldModelHasValue(model: any, field: FormlyFieldConfig, parentTabKey: string | null): boolean
  {
    const existing = this.getFieldModelValue(model, field, parentTabKey);
    if (field.type === 'checkbox')
    {
      return existing !== undefined && existing !== null;
    }

    if (field.type === 'multiselect')
    {
      if (typeof existing === 'string')
      {
        return false;
      }

      return Array.isArray(existing) && existing.length > 0;
    }

    if (field.type === 'multicheckbox')
    {
      if (typeof existing === 'string')
      {
        return false;
      }

      // Explicit true/false maps count as answered (including all-false after uncheck).
      return this.isMulticheckboxControlValue(existing);
    }

    return existing !== undefined && existing !== null && existing !== '';
  }

  private formControlHasValue(field: FormlyFieldConfig): boolean
  {
    const current = field.formControl?.value;
    if (field.type === 'checkbox')
    {
      return current !== undefined && current !== null;
    }

    if (field.type === 'multiselect')
    {
      if (typeof current === 'string')
      {
        return false;
      }

      return Array.isArray(current) && current.length > 0;
    }

    if (field.type === 'multicheckbox')
    {
      if (typeof current === 'string')
      {
        return false;
      }

      // Explicit true/false maps count as answered (including all-false after uncheck).
      return this.isMulticheckboxControlValue(current);
    }

    return current !== undefined && current !== null && current !== '';
  }

  private getFieldModelKey(field: FormlyFieldConfig): string | number | null
  {
    if (field.key === undefined || field.key === null)
    {
      return null;
    }

    return Array.isArray(field.key) ? field.key.join('.') : field.key;
  }

  private getFieldModelValue(model: any, field: FormlyFieldConfig, parentTabKey: string | null): any
  {
    const key = this.getFieldModelKey(field);
    if (key === null || !model)
    {
      return undefined;
    }

    if (parentTabKey && (field as any).tab)
    {
      return model[parentTabKey]?.[(field as any).tab]?.[key];
    }

    return model[key];
  }

  private setFieldModelValue(model: any, field: FormlyFieldConfig, value: any, parentTabKey: string | null): void
  {
    const key = this.getFieldModelKey(field);
    if (key === null || !model)
    {
      return;
    }

    if (parentTabKey && (field as any).tab)
    {
      if (model[parentTabKey] === undefined)
      {
        model[parentTabKey] = {};
      }
      if (model[parentTabKey][(field as any).tab] === undefined)
      {
        model[parentTabKey][(field as any).tab] = {};
      }
      model[parentTabKey][(field as any).tab][key] = value;
      return;
    }

    model[key] = value;
  }

  private getParentTabKeyFromField(field: FormlyFieldConfig): string | null
  {
    let parent = field.parent;
    while (parent)
    {
      if (parent.type === 'tabs' && parent.key)
      {
        return String(parent.key);
      }
      parent = parent.parent;
    }
    return null;
  }

  private applyInterpolatedExpression(field: FormlyFieldConfig, property: string, value: string, formState?: any): void
  {
    if (typeof value !== 'string' || !value.includes('${'))
    {
      return;
    }

    if (field.expressionProperties?.[property])
    {
      const expressionProperties = { ...field.expressionProperties };
      delete expressionProperties[property];
      field.expressionProperties = Object.keys(expressionProperties).length > 0 ? expressionProperties : undefined;
    }

    const sources = (field as any).__interpolatedDisplaySources as Record<string, string> | undefined;
    if (!sources)
    {
      (field as any).__interpolatedDisplaySources = {};
    }
    (field as any).__interpolatedDisplaySources[property] = value;

    const resolved = this.finalizeResolvedDisplayString(
      this.resolveInterpolatedString(value, field, field.model, formState));
    this.setInterpolatedDisplayProperty(field, property, resolved);
  }

  private setInterpolatedDisplayProperty(field: FormlyFieldConfig, property: string, text: string): void
  {
    if (property === 'templateOptions.label')
    {
      if (!field.templateOptions)
      {
        field.templateOptions = {};
      }
      field.templateOptions = { ...field.templateOptions, label: text };
    }
    else if (property === 'templateOptions.description')
    {
      if (!field.templateOptions)
      {
        field.templateOptions = {};
      }
      const templateOptions = { ...field.templateOptions, description: text };
      if (field.type === 'label')
      {
        templateOptions.label = '';
        (templateOptions as { hideLabel?: boolean }).hideLabel = true;
      }
      field.templateOptions = templateOptions;
    }
    else if (property === 'template')
    {
      field.template = text;
    }

    field.options?.checkExpressions?.(field);
  }

  private syncInterpolatedDisplayProperties(fields: FormlyFieldConfig[]): void
  {
    const walk = (field: FormlyFieldConfig): void =>
    {
      const sources = (field as any).__interpolatedDisplaySources as Record<string, string> | undefined;
      if (sources)
      {
        Object.entries(sources).forEach(([property, source]) =>
        {
          const resolved = this.finalizeResolvedDisplayString(
            this.resolveInterpolatedString(source, field, field.model, this.resolveInterpolationFormState(field)));
          this.setInterpolatedDisplayProperty(field, property, resolved);
        });
      }

      field.fieldGroup?.forEach(walk);
    };

    fields.forEach(walk);
  }

  private resolveInterpolationFormState(field: FormlyFieldConfig, formStateOrOptions?: any): any
  {
    if (formStateOrOptions?.formState !== undefined)
    {
      return formStateOrOptions.formState;
    }

    return formStateOrOptions ?? field.options?.formState ?? (field as any).__interpolationFormState;
  }

  private evaluateInterpolatedExpression(expression: string, field: FormlyFieldConfig, model?: any, formState?: any): any
  {
    const resolvedFormState = this.resolveInterpolationFormState(field, formState);
    const evalField = {
      ...field,
      model: model ?? field.model,
      options: {
        ...(field.options ?? {}),
        formState: resolvedFormState
      }
    };

    const patient = resolvedFormState?.patient ?? null;
    const normalizedExpression = this.normalizeInterpolatedExpression(expression);

    try
    {
      const getIdentifier = (code: string): string => this.getIdentifierValue(patient, code);

      const result = new Function(
        'field',
        'model',
        'formState',
        'patient',
        'getIdentifier',
        `return (${normalizedExpression});`
      )(
        evalField,
        model ?? field.model,
        resolvedFormState,
        patient,
        getIdentifier
      );

      if (result !== undefined && result !== null)
      {
        return result;
      }
    }
    catch
    {
      // fall through to identifier helper
    }

    const identifierCode = this.extractIdentifierCodeFromExpression(normalizedExpression);
    if (identifierCode)
    {
      return this.getIdentifierValue(patient, identifierCode);
    }

    return '';
  }

  /**
   * Kendo HTML editor often entity-encodes quotes inside ${...}, which breaks eval (e.g. 'MA' -> &apos;MA&apos;).
   */
  private normalizeInterpolatedExpression(expression: string): string
  {
    return expression
      .replace(/&apos;/gi, '\'')
      .replace(/&#0*39;/gi, '\'')
      .replace(/&#x27;/gi, '\'')
      .replace(/&quot;/gi, '"')
      .replace(/&#0*34;/gi, '"')
      .replace(/[\u2018\u2019]/g, '\'')
      .replace(/[\u201C\u201D]/g, '"');
  }

  private extractIdentifierCodeFromExpression(expression: string): string | null
  {
    const helperMatch = expression.match(/getIdentifier\s*\(\s*['"]([^'"]+)['"]\s*\)/);
    if (helperMatch?.[1])
    {
      return helperMatch[1];
    }

    const dictionaryMatch = expression.match(/dictionary\?\.code\s*===\s*['"]([^'"]+)['"]/);
    return dictionaryMatch?.[1] ?? null;
  }

  private getIdentifierValue(patient: any, code: string): string
  {
    if (!patient?.identifiers?.length || !code)
    {
      return '';
    }

    const item = patient.identifiers.find((identifier: any) =>
    {
      const dictionary = identifier?.dictionary;
      if (!dictionary)
      {
        return false;
      }

      return dictionary.code === code
        || dictionary.alternateCode === code
        || String(dictionary.code ?? '').toUpperCase() === String(code).toUpperCase();
    });

    return item?.value ?? '';
  }

  private refreshFormlyFieldExpressions(options: FormlyFormOptions, field: FormlyFieldConfig): void
  {
    options.checkExpressions(field);
    field.fieldGroup?.forEach((child) => this.refreshFormlyFieldExpressions(options, child));
  }

  /**
   * This recursive function extract all fields including all controls in all tabs (fieldGroup)
   * 
   * @param f Field record to process.
   */
  private processAssessmentField(f: any): void 
  {
    if (f.fieldGroup) 
    {
      f.fieldGroup.forEach((t: any) => { this.processAssessmentField(t); });
    }
    else if (f.key)
    {
      this.flatFields.push(f);
    }
  }

  private processFormlyFormControls(sumKeys: any, form: any, formControls: any): void 
  {
    if (form.controls) 
    {
      for (const key of Object.keys(form.controls)) 
      {
        this.addFormlyValueChange(sumKeys, key, form);
        this.processFormlyFormControls(sumKeys, form.controls[key], formControls);
      }
    }
    else 
    {
      for (const key of Object.keys(formControls)) 
      {
        this.addFormlyValueChange(sumKeys, key, formControls);
      }
    }
  }

  private addFormlyValueChange(sumKeys: any, key: string, form: UntypedFormGroup): void 
  {
    let foundKey = null;
    sumKeys.forEach((x: any) => { if (x.fields.includes(key)) { foundKey = x; } });
    if (foundKey) 
    {
      const formControls = form.controls ? form.controls : form;
      formControls[key].valueChanges.subscribe(() =>
      {
        let sumTotal = 0;
        // Use replace to keep only numeric within value (Example: Frequently5 = will sum as 5)
        foundKey.fields.forEach((x: any) =>
        {
          if (typeof formControls[x].value === 'number')
          {
            sumTotal += (formControls[x].value ? formControls[x].value : 0) | 0;
          }
          else
          {
            sumTotal += (formControls[x].value ? +formControls[x].value.replace(/\D/g, '') : null) | 0;
          }
        });
        formControls[foundKey.sumKey].setValue(sumTotal);
      });
    }
  }

  /**
   * This recursive function extract all fields including all controls in all tabs (fieldGroup)
   * 
   * @param f Field record to process.
   */
  private processAssessmentFieldSum(f: any, form: any, sumKeys: any): void 
  {
    if (f.fieldGroup) 
    {
      f.fieldGroup.forEach((t: any) => { this.processAssessmentFieldSum(t, form, sumKeys); });
    }
    else if (f.key)
    {
      if (f && f.templateOptions && f.templateOptions.sumKey) 
      {
        const foundSum = sumKeys.find((x: any) => x.sumKey === f.templateOptions.sumKey);
        if (!foundSum) 
        {
          sumKeys.push({ sumKey: f.templateOptions.sumKey, fields: [f.key] });
        }
        else
        {
          foundSum.fields.push(f.key);
        }
      }
    }
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    this.translate.get(['COMMUNITY-RESTRICTION-NONE', 'COMMUNITY-RESTRICTION-HIDDEN', 'COMMUNITY-RESTRICTION-READONLY']).subscribe({
      next: (res) =>
      {
        this.communityUserRestrictions[0].text = res['COMMUNITY-RESTRICTION-NONE'];
        this.communityUserRestrictions[1].text = res['COMMUNITY-RESTRICTION-HIDDEN'];
        this.communityUserRestrictions[2].text = res['COMMUNITY-RESTRICTION-READONLY'];
      }
    });
  }
}
